/**
 * Organization Storage Service
 * Handles file operations using the organization's connected blob storage
 */

import { apiClient } from './api'
import { readRefusal } from './api-client'

// Types for storage operations
export interface StorageInfo {
  connected: boolean
  account_name?: string
  containers?: string[]
}

export interface BlobInfo {
  name: string
  size: number
  content_type: string
  last_modified: string
  etag?: string
}

export interface ListBlobsResponse {
  blobs: BlobInfo[]
  total: number
  container: string
  prefix: string
  has_more: boolean
}

export interface UploadBlobResponse {
  blob_url: string
  container: string
  path: string
  size: number
  content_type: string
  etag?: string
  created_at: string
}

export interface StorageTestResult {
  status: 'healthy' | 'unhealthy'
  account_name?: string
  containers?: string[]
  message: string
  tested_at: string
}

/**
 * Storage API service for organization blob storage operations
 */
export const storageService = {
  /**
   * Check if the organization has connected storage
   * This is the key method to determine if file upload is available
   */
  getStorageInfo: async (): Promise<StorageInfo> => {
    try {
      return await apiClient.get<StorageInfo>('/v1/governance/storage/info')
    } catch {
      // If storage not connected, return disconnected state
      console.log('[Storage] No storage connected for organization')
      return { connected: false }
    }
  },

  /**
   * Test the storage connection
   */
  testConnection: async (): Promise<StorageTestResult> => {
    return apiClient.post<StorageTestResult>('/v1/governance/storage/test')
  },

  /**
   * Upload a file to organization storage
   * Uses user-specific path: users/{userId}/uploads/{filename}
   */
  uploadFile: async (
    file: File,
    options?: {
      container?: string
      path?: string
      onProgress?: (progress: number) => void
    }
  ): Promise<UploadBlobResponse> => {
    const formData = new FormData()
    formData.append('file', file)

    // Use provided path or generate user-specific path
    const blobPath = options?.path || `uploads/${Date.now()}-${file.name}`
    formData.append('path', blobPath)

    if (options?.container) {
      formData.append('container', options.container)
    }

    // Note: Progress tracking would require XHR or fetch with ReadableStream
    // For now, we use a simple upload
    const response = await fetch(
      `${apiClient['baseURL']}/v1/governance/storage/upload`,
      {
        method: 'POST',
        credentials: 'include',
        body: formData,
      }
    )

    if (!response.ok) {
      // governance-service puts the status text in error and the sentence in
      // message; readRefusal takes the sentence.
      throw new Error((await readRefusal(response)) ?? 'Failed to upload file')
    }

    return response.json()
  },

  /**
   * Upload multiple files to organization storage
   */
  uploadFiles: async (
    files: File[],
    options?: {
      container?: string
      pathPrefix?: string
      onProgress?: (fileIndex: number, progress: number) => void
    }
  ): Promise<UploadBlobResponse[]> => {
    const results: UploadBlobResponse[] = []

    for (let i = 0; i < files.length; i++) {
      const file = files[i]!
      const path = options?.pathPrefix
        ? `${options.pathPrefix}/${Date.now()}-${file.name}`
        : `uploads/${Date.now()}-${file.name}`

      try {
        const result = await storageService.uploadFile(file, {
          container: options?.container,
          path,
          onProgress: options?.onProgress
            ? (progress) => options.onProgress!(i, progress)
            : undefined,
        })
        results.push(result)
      } catch (error) {
        console.error(`Failed to upload file ${file.name}:`, error)
        throw error
      }
    }

    return results
  },

  /**
   * List files in storage
   */
  listFiles: async (options?: {
    container?: string
    prefix?: string
    maxItems?: number
  }): Promise<ListBlobsResponse> => {
    const params: Record<string, string> = {}
    if (options?.container) params.container = options.container
    if (options?.prefix) params.prefix = options.prefix
    if (options?.maxItems) params.max_items = options.maxItems.toString()

    return apiClient.get<ListBlobsResponse>('/v1/governance/storage/list', params)
  },

  /**
   * Get download URL for a file
   * Returns the path to download endpoint
   */
  getDownloadUrl: (container: string, path: string): string => {
    return `/v1/governance/storage/download/${container}/${path}`
  },

  /**
   * Download a file from storage
   */
  downloadFile: async (container: string, path: string): Promise<Blob> => {
    const response = await fetch(
      `${apiClient['baseURL']}/v1/governance/storage/download/${container}/${path}`,
      {
        credentials: 'include',
      }
    )

    if (!response.ok) {
      throw new Error('Failed to download file')
    }

    return response.blob()
  },

  /**
   * Delete a file from storage
   */
  deleteFile: async (container: string, path: string): Promise<void> => {
    await apiClient.delete(`/v1/governance/storage/${container}/${path}`)
  },
}

export default storageService
